import { Box, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { useDarkMode, useMediaUp } from 'hooks';
import {
  codeCardBg,
  edgeIcon,
  firefoxIcon,
  googleIcon,
  macbookPerformance,
  macbookPerformance2x,
  safariIcon,
  screenImg1,
} from 'images/retail-energy';
import React, { FC } from 'react';
import { CheckCircleIcon, LockIcon, ZeroPercentIcon } from 'svgs';
import { FeatureBox, FeatureTextContent } from 'templates/retail-energy';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(10, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(20, 0),
    },
  },
  fullWidthBoxImage: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(8, 0, 6, 4),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      padding: theme.spacing(12, 0, 6, 8),
    },
  },
  fullWidthTextContent: {
    padding: theme.spacing(0, 6),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 12),
    },
  },
  checkCard: {
    backgroundImage: theme.palette.gradients.lightDarkBlue,
    color: theme.palette.common.white,
  },
  lockCard: {
    backgroundColor: theme.palette.switchedColors.reverseBg,
    color: theme.palette.switchedColors.simpleCard,
    '& path': {
      fill: (isDarkMode: boolean) => (isDarkMode ? theme.palette.secondary.dark : theme.palette.common.white),
    },
  },
  onlyColorTitle: {
    color: theme.palette.common.white,
  },
  reverseColorTitle: {
    color: theme.palette.switchedColors.reverseTitle,
  },
  onlyImageLine: {
    marginTop: theme.spacing(4),
    maxWidth: 351,
    margin: theme.spacing(4, 'auto', 0),
    display: 'block',
    borderRadius: theme.spacing(4),
    boxShadow: '0px 55px 34px -41px rgba(0, 0, 0, 0.25)',
  },
  imgFull: {
    maxHeight: 180,
    objectFit: 'contain',
    display: 'block',
    marginLeft: 'auto',
    marginTop: theme.spacing(12),
    [theme.breakpoints.up('sm')]: {
      maxHeight: 240,
    },
    [theme.breakpoints.up('md')]: {
      maxHeight: 'inherit',
      marginTop: 0,
    },
  },
  performanceTitle: {
    fontSize: 68,
    color: theme.palette.primary.light,
  },
  textGrid: {
    textAlign: 'center',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
    },
  },
  alignCenter: {
    textAlign: 'center',
  },
  optimizedCard: {
    background: `url(${codeCardBg})`,
    backgroundSize: 'cover',
    '& *': {
      color: theme.palette.common.white,
    },
  },
}));

const browserIcons = [googleIcon, firefoxIcon, safariIcon, edgeIcon];

const FeaturesList: FC = () => {
  const isMedium = useMediaUp('md');
  const { isDarkMode } = useDarkMode();
  const classes = useStyles(isDarkMode);

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Grid container justifyContent="center">
          <Grid lg={10} xs={12} item>
            <Grid container spacing={6}>
              {/* TODO Temporarily removed section, uncomment if is needed */}
              {/* <FeatureBox className={classes.fullWidthBoxImage} xs={12}>
                <FeatureTextContent
                  className={classes.fullWidthTextContent}
                  title={{ text: 'Adaptive design', lgBigSize: 48 }}
                  text={{
                    text: "Energy5 features a user interface that's adapted to all screen sizes. It means you'll be able to access the best energy deals on the market wherever and whenever you please! Whether that's a phone, tablet, computer, or something in between, the user can benefit from all the advantages our Energy Management platform has to offer.",
                  }}
                />
                <img className={classes.imgFull} src={e51Image} alt="e5" />
              </FeatureBox> */}
              <FeatureBox md={5} xs={12} className={classes.checkCard}>
                <FeatureTextContent
                  icon={<CheckCircleIcon />}
                  title={{ text: 'SEO optimisation', className: classes.onlyColorTitle }}
                  text={{
                    text: "Energy5 boasts SEO optimization that improves our web page to increase its visibility when people search for relevant services in Google, Bing, and other search engines. Our website's content is fully optimized according to the highest industry standards, garnering attention and attracting prospective and existing customers to our platform.",
                  }}
                />
              </FeatureBox>
              <FeatureBox md={7} xs={12}>
                <FeatureTextContent
                  title={{ text: 'Intuitive user experience' }}
                  text={{
                    text: "An intuitive interface works the way the user expects it to. In other words, we find something intuitive when we don't have to think about it. It is a design that is easy to use, and the user knows what to do when they see it. Energy5 follows all the latest UX trends and tendencies to provide the user with a flawless and straightforward experience that will not leave them wondering in the process.",
                  }}
                >
                  <img src={screenImg1} alt="choose available plans" width="100%" className={classes.onlyImageLine} />
                </FeatureTextContent>
              </FeatureBox>
              <FeatureBox md={7} xs={12}>
                <FeatureTextContent
                  title={{ text: 'Lightning-fast loading' }}
                  text={{
                    text: "In this day and age, it's extremely important to ensure that your web product is accessible to everyone. Loading speed is an indicator of how accessible your web product is in the first place. Energy5 is a highly adaptable online platform that boasts lightning-fast loading speed, allowing users to benefit from a fast and seamless experience.",
                  }}
                >
                  <Box mt={isMedium ? 15 : 6} />
                  <Grid spacing={5} alignItems="center" direction={isMedium ? 'row' : 'column-reverse'} container>
                    <Grid md={6} xs={12} item>
                      <img
                        src={macbookPerformance}
                        alt="macbook-performance"
                        srcSet={`${macbookPerformance} 516w, ${macbookPerformance2x} 1032w`}
                        width="100%"
                      />
                    </Grid>
                    <Grid md={6} xs={12} item className={classes.textGrid}>
                      <Typography variant="h1" component="p" className={classes.performanceTitle}>
                        93%
                      </Typography>
                      <Typography variant="body2">Lorem ipsum dolor sit amet, consectetur adipiscing elit.</Typography>
                    </Grid>
                  </Grid>
                </FeatureTextContent>
              </FeatureBox>
              <FeatureBox md={5} xs={12} className={classes.lockCard}>
                <FeatureTextContent
                  icon={<LockIcon />}
                  title={{ text: '100% secure online platform', className: classes.reverseColorTitle }}
                  text={{
                    text: 'At Energy5, we put greater emphasis on cyber-security – both for our users and our own web solutions. We employ the most sophisticated and lucrative security protocols to guarantee a hassle-free experience for anyone interested in saving big on energy deals. Our IT specialists work tirelessly to enhance the security of our website visitors and platform users alike.',
                  }}
                />
              </FeatureBox>
              <FeatureBox md={5} xs={12}>
                <FeatureTextContent title={{ text: 'Advanced browser \n compatibility', className: classes.alignCenter }} />
                <Box display="flex" justifyContent="center">
                  <Box display="grid" gridTemplateColumns="repeat(2, 2fr)">
                    {browserIcons.map((icon, index) => (
                      <Box width={80} height={80} key={index} m={1.5} style={{ backgroundImage: `url(${icon})` }} />
                    ))}
                  </Box>
                </Box>
              </FeatureBox>
              <FeatureBox md={7} xs={12} className={classes.optimizedCard}>
                <FeatureTextContent title={{ text: 'Fully optimized \n code', className: classes.alignCenter }} />
                <Box display="flex" justifyContent="center" mt={9}>
                  <ZeroPercentIcon />
                </Box>
              </FeatureBox>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesList;
